import React, { useState } from 'react';
import { Input, Select, message as antMessage } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import {
  CATEGORY_LABEL,
  CreateTicketInput,
  SupportTicketCategory,
  SupportUploadLimits,
} from '../../services/supportApi';
import { useAttachSlot } from './attach';
import Composer from './Composer';
import * as S from './styles';

interface Props {
  limits: SupportUploadLimits;
  onCreate: (input: CreateTicketInput, onProgress: (p: number) => void) => Promise<unknown>;
}

const CATEGORY_OPTIONS = (Object.keys(CATEGORY_LABEL) as SupportTicketCategory[]).map((c) => ({
  value: c,
  label: CATEGORY_LABEL[c],
}));

/** Subject + category live above the same composer used for replies, so "first message with
 *  screenshots" and "reply with screenshots" share one code path. */
const NewTicketForm: React.FC<Props> = ({ limits, onCreate }) => {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<SupportTicketCategory>('BUG');
  const attach = useAttachSlot();

  const submit = async (body: string, files: File[], onProgress: (p: number) => void) => {
    const s = subject.trim();
    if (!s) {
      antMessage.warning('Give your report a short subject.');
      throw new Error('subject required');
    }
    await onCreate({ subject: s, category, body, files }, onProgress);
  };

  return (
    <>
      <S.FormBody>
        <S.FieldLabel htmlFor="support-subject">Subject</S.FieldLabel>
        <Input
          id="support-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="e.g. Grid bot stopped placing orders"
          maxLength={200}
          autoFocus
        />
        <S.FieldLabel htmlFor="support-category">Category</S.FieldLabel>
        <Select
          id="support-category"
          value={category}
          onChange={setCategory}
          options={CATEGORY_OPTIONS}
          style={{ width: '100%' }}
        />
        <S.FieldLabel>Details</S.FieldLabel>
        <S.Hint style={{ marginTop: 0 }}>
          Describe what happened, what you expected, and when. A screenshot or a short screen
          recording helps us reproduce it faster.
        </S.Hint>
        {/* Visual target only: the drop itself is handled once, by the panel, so a file dropped
            here is not attached twice by a nested handler. */}
        <S.DropZone
          type="button"
          onClick={() => attach.current?.openPicker()}
          aria-label="Add a screenshot or a screen recording"
          data-testid="support-dropzone"
        >
          <S.DropZoneIcon>
            <CloudUploadOutlined />
          </S.DropZoneIcon>
          Drag a screenshot or recording here
          <S.DropZoneHint>or click to browse · you can also paste from the clipboard</S.DropZoneHint>
        </S.DropZone>
      </S.FormBody>
      <S.Footer>
        <Composer limits={limits} onSend={submit} placeholder="What went wrong?" />
      </S.Footer>
    </>
  );
};

export default NewTicketForm;
